import { z } from 'zod';

/**
 * Where the member was standing when they reported somebody.
 *
 * It is **not** the evidence and it is not a second reason code: it tells the
 * reviewer which surface to open first, and it is what lets a report raised from
 * a conversation carry the conversation's id so nobody has to search for it.
 *
 * - `profile` — the member detail screen, or the member's own blocked list.
 * - `nomination` — today's card stack, before either of them decided anything.
 * - `conversation` — inside a chat, which is the context that carries a ref.
 */
export const REPORT_CONTEXTS = ['profile', 'nomination', 'conversation'] as const;

export const ReportContextSchema = z.enum(REPORT_CONTEXTS);

export type ReportContext = (typeof REPORT_CONTEXTS)[number];

/**
 * Does this context point at a row the reviewer can open?
 *
 * Only `conversation` does, and its ref is the conversation id. `profile` and
 * `nomination` are already fully addressed by the reported member's own id —
 * a nomination ref would name a row that expires the next day, which is a link
 * that rots in the queue rather than evidence.
 *
 * Expressed as a predicate over the enum rather than as a rule written at the
 * call site, so {@link ReportCreateSchema}'s refinement and the api's validation
 * are one fact with one spelling.
 */
export function contextCarriesRef(context: ReportContext): boolean {
  return context === 'conversation';
}
